/**
 * Blast-Radius-Aware Autonomous Remediation (Patent Sub-system O)
 * ================================================================
 * The reason engineering teams ignore FinOps auto-remediation: nobody trusts
 * a robot that doesn't know which services it might break. We do.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, AlertTriangle, AlertOctagon, RotateCcw, Loader2, FlaskConical,
  Workflow, GitBranch, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  if (n == null) return '$0';
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const DECISION_COLORS = {
  AUTO_EXECUTE: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', text: 'text-emerald-300', icon: ShieldCheck },
  AUTO_EXECUTE_WITH_MONITORING: { bg: 'bg-amber-500/10', border: 'border-amber-500/40', text: 'text-amber-300', icon: AlertTriangle },
  REQUIRES_HUMAN_APPROVAL: { bg: 'bg-red-500/10', border: 'border-red-500/40', text: 'text-red-300', icon: AlertOctagon },
};

const SafeAutonomy = () => {
  const [queue, setQueue] = useState(null);
  const [rollbacks, setRollbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/safe-autonomy/action-queue`),
      axios.get(`${API_URL}/safe-autonomy/rollback-events`),
    ]).then(([q, r]) => {
      setQueue(q.data);
      setRollbacks(r.data.events || []);
      setSelected(q.data.actions[0]);
    }).catch(() => toast.error('Could not load action queue'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="safe-autonomy-page">
      <Helmet>
        <title>Safe Autonomy — Blast-Radius-Aware Auto-Remediation | CARTIEAI</title>
        <meta name="description" content="The auto-remediation engineers actually trust. We tie every automated action to a service-graph blast radius and auto-rollback on SLI degradation. Patent-pending sub-system O." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="sa-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Safe Autonomy</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            ProsperOps, Spot.io, Vantage auto-execute commitment optimizations. None tie the auto-action to a <span className="text-white font-semibold">service-graph blast radius</span> AND auto-rollback on SLI degradation. <span className="text-emerald-300">We do.</span>
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Every proposed savings action gets a risk score: <code className="text-emerald-300 bg-slate-900 px-1 rounded">blast_radius × (1 − sli_confidence) × reversibility_penalty</code>. Within the configured risk budget → auto-execute with monitoring. Beyond → human approval. Any SLI degrades &gt;2σ in the observation window → auto-rollback.
          </p>
        </div>

        {/* SUMMARY */}
        {queue && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total potential savings</div>
                <div className="text-2xl font-bold text-white mt-1">{fmtUsd(queue.total_potential_savings_usd)}/mo</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-emerald-500/30">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">Auto-executable</div>
                <div className="text-2xl font-bold text-emerald-300 mt-1" data-testid="sa-auto-exec-savings">{fmtUsd(queue.auto_executable_savings_usd)}/mo</div>
                <div className="text-[10px] text-slate-500 mt-1">within risk budget</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Risk budget</div>
                <div className="text-2xl font-bold text-white mt-1">{queue.risk_budget}</div>
                <div className="text-[10px] text-slate-500 mt-1">configurable per tenant</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Auto-rollbacks (30d)</div>
                <div className="text-2xl font-bold text-white mt-1">{rollbacks.length}</div>
                <div className="text-[10px] text-slate-500 mt-1">caught by SLI degradation</div>
              </CardContent>
            </Card>
          </div>
        )}

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-16 text-slate-500" />}

        {/* ACTION QUEUE + DETAIL */}
        {queue && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
            <Card className="lg:col-span-2 bg-slate-900/60 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-emerald-300" /> Action queue
                </CardTitle>
                <CardDescription>Sorted by risk score. Click to see the blast-radius graph.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {queue.actions.map((a) => {
                  const style = DECISION_COLORS[a.decision.decision] || DECISION_COLORS.AUTO_EXECUTE;
                  const Icon = style.icon;
                  const isSel = selected?.action_id === a.action_id;
                  return (
                    <button
                      key={a.action_id}
                      onClick={() => setSelected(a)}
                      className={`w-full text-left px-3 py-2.5 rounded border transition ${isSel ? style.border + ' ' + style.bg : 'border-white/10 hover:bg-slate-950/60'}`}
                      data-testid={`sa-action-${a.action_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${style.text}`} />
                          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{a.action_id}</span>
                        </div>
                        <span className="text-xs font-mono text-white font-semibold">{fmtUsd(a.projected_savings_monthly_usd)}/mo</span>
                      </div>
                      <div className="text-sm text-white">{a.description}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-[10px] font-semibold uppercase ${style.text}`}>{a.decision.label}</span>
                        <span className="text-[10px] text-slate-500 font-mono">risk={a.risk_score.raw_score}</span>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {/* DETAIL */}
            {selected && (() => {
              const style = DECISION_COLORS[selected.decision.decision] || DECISION_COLORS.AUTO_EXECUTE;
              const Icon = style.icon;
              return (
                <Card className="lg:col-span-3 bg-slate-900/60 border-white/10" data-testid="sa-detail-panel">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${style.text}`} /> {selected.action_id}
                    </CardTitle>
                    <CardDescription>{selected.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className={`rounded-md border ${style.border} ${style.bg} p-4 mb-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-lg font-bold ${style.text}`}>{selected.decision.label}</span>
                        <Badge variant="outline" className="border-white/10 text-slate-400 font-mono">
                          risk score {selected.risk_score.raw_score}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed">{selected.rationale}</p>
                    </div>

                    {/* Blast radius breakdown */}
                    <div className="mb-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Blast radius</div>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-center">
                          <div className="text-[10px] uppercase text-red-300">Tier 0 (critical)</div>
                          <div className="text-white font-mono font-bold text-lg">{selected.blast_radius.tier_0_services.length}</div>
                        </div>
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-center">
                          <div className="text-[10px] uppercase text-amber-300">Tier 1</div>
                          <div className="text-white font-mono font-bold text-lg">{selected.blast_radius.tier_1_services.length}</div>
                        </div>
                        <div className="bg-slate-500/10 border border-slate-500/30 rounded p-2 text-center">
                          <div className="text-[10px] uppercase text-slate-400">Tier 2</div>
                          <div className="text-white font-mono font-bold text-lg">{selected.blast_radius.tier_2_services.length}</div>
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-2">
                        <span className="text-red-300">Tier-0 services affected:</span> {selected.blast_radius.tier_0_services.join(', ') || 'none'}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        <span className="text-amber-300">Tier-1 services affected:</span> {selected.blast_radius.tier_1_services.join(', ') || 'none'}
                      </div>
                    </div>

                    {/* SLI confidence */}
                    <div className="mb-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">SLI stability of affected services</div>
                      <div className="space-y-1.5">
                        {Object.entries(selected.sli_confidence.per_service).map(([svc, sli]) => (
                          <div key={svc} className="flex items-center gap-3 text-xs">
                            <span className="text-slate-300 font-mono w-44 truncate">{svc}</span>
                            <div className="flex-1 h-1.5 bg-slate-950 rounded overflow-hidden">
                              <div className={`h-full ${sli.stability > 0.85 ? 'bg-emerald-400' : sli.stability > 0.70 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${sli.stability * 100}%` }} />
                            </div>
                            <span className="text-slate-500 font-mono w-12 text-right">{(sli.stability * 100).toFixed(0)}%</span>
                            <span className="text-slate-300 font-mono w-16 text-right text-[10px]">p99 {sli.p99_baseline}ms</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Reversibility */}
                    <div className="mb-2 flex items-center justify-between text-xs">
                      <span className="text-slate-400">Reversibility:</span>
                      <Badge variant="outline" className="border-white/10 font-mono text-slate-300">
                        {selected.risk_score.reversibility} (×{selected.risk_score.reversibility_penalty})
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        )}

        {/* AUTO-ROLLBACK EVENTS */}
        <Card className="mt-12 bg-slate-900/60 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-300" /> Recent auto-rollbacks
            </CardTitle>
            <CardDescription>When the system detected SLI degradation and reverted automatically. The lesson learned feeds back into the catalog.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="sa-rollbacks">
              {rollbacks.map((rb) => (
                <div key={rb.id} className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-white font-semibold">{rb.action_description}</span>
                    <span className="text-[10px] font-mono text-slate-500">{new Date(rb.ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div className="text-[11px] text-amber-200 mb-1.5"><strong>Rollback trigger:</strong> {rb.rollback_trigger}</div>
                  <div className="text-[11px] text-slate-400 mb-1.5"><strong>Savings lost:</strong> ${rb.savings_lost_usd}/mo (correctly chose safety over savings)</div>
                  <div className="text-[11px] text-emerald-200"><strong>Lesson:</strong> {rb.lesson}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SafeAutonomy;
